import React, { useState } from "react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { toast } from "sonner";
import ProfilePhotoEditor from "../components/ProfilePhotoEditor";
import { Lock, User, Camera, AtSign, Pencil } from "lucide-react";

export default function MyProfilePage() {
  const { user, refresh, hasCapability } = useAuth();
  const [current, setCurrent] = useState("");
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [emailEditing, setEmailEditing] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [savingEmail, setSavingEmail] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (pw1.length < 6) return toast.error("New password must be at least 6 characters.");
    if (pw1 !== pw2) return toast.error("Passwords do not match.");
    setSubmitting(true);
    try {
      await api.post("/me/change-password", { current_password: current, new_password: pw1 });
      toast.success("Password updated");
      setCurrent(""); setPw1(""); setPw2("");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update password");
    } finally {
      setSubmitting(false);
    }
  };

  const startEmailEdit = () => {
    setNewEmail(user.email);
    setEmailEditing(true);
  };

  const cancelEmailEdit = () => {
    if (savingEmail) return;
    setEmailEditing(false);
    setNewEmail("");
  };

  const onSaveEmail = async () => {
    const next = (newEmail || "").trim().toLowerCase();
    if (!next || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(next)) {
      return toast.error("Enter a valid email address.");
    }
    if (next === (user.email || "").toLowerCase()) {
      setEmailEditing(false);
      return toast("Email unchanged.");
    }
    if (!window.confirm(
      `Change your login email to:\n  ${next}\n\nThis will also change your LOGIN USERNAME.\nProceed?`
    )) return;
    setSavingEmail(true);
    try {
      await api.patch("/me/email", { new_email: next });
      toast.success("Email updated");
      setEmailEditing(false);
      setNewEmail("");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update email");
    } finally {
      setSavingEmail(false);
    }
  };

  if (!user) return null;

  const homes = (user.assigned_homes && user.assigned_homes.length ? user.assigned_homes : [user.assigned_home]).filter(Boolean);
  const canEditOwnEmail = hasCapability("User Management", "Edit Self Email");

  return (
    <div className="p-6 sm:p-8 space-y-6 max-w-4xl">
      <div>
        <p className="text-small-caps">Account</p>
        <h1 className="text-h1 mt-1">My Profile</h1>
      </div>

      <section className="nv-card p-6">
        <div className="flex items-center gap-2 text-slate-700 font-semibold text-sm mb-4">
          <Camera size={16} className="text-sky-700" /> Profile picture
        </div>
        <ProfilePhotoEditor />
      </section>

      <section className="nv-card p-6">
        <div className="flex items-center gap-2 text-slate-700 font-semibold text-sm mb-4">
          <User size={16} className="text-sky-700" /> Profile
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5 text-sm">
          <Field label="Full Name" value={user.full_name} testId="profile-name" />

          <div>
            <p className="text-small-caps">Email</p>
            {emailEditing ? (
              <div className="mt-1 space-y-2" data-testid="profile-email-editor">
                <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  <strong>Heads up:</strong> changing this email will also change your <strong>login username</strong>.
                </div>
                <input
                  type="email"
                  autoFocus
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  className="w-full text-sm rounded-md border border-slate-200 bg-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500/30"
                  data-testid="profile-email-input"
                />
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={onSaveEmail}
                    disabled={savingEmail}
                    className="px-3 py-1.5 rounded-md text-xs font-semibold bg-indigo-700 hover:bg-indigo-800 text-white disabled:opacity-60"
                    data-testid="profile-email-save-btn"
                  >
                    {savingEmail ? "Saving…" : "Save email"}
                  </button>
                  <button
                    type="button"
                    onClick={cancelEmailEdit}
                    disabled={savingEmail}
                    className="px-3 py-1.5 rounded-md text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                    data-testid="profile-email-cancel-btn"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="mt-1 flex items-center gap-2">
                <p className="text-slate-800 font-medium" data-testid="profile-email">{user.email}</p>
                {canEditOwnEmail && (
                  <button
                    type="button"
                    onClick={startEmailEdit}
                    title="Change email"
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs text-indigo-700 hover:bg-indigo-50"
                    data-testid="profile-email-edit-btn"
                  >
                    <Pencil size={12} /> Edit
                  </button>
                )}
              </div>
            )}
          </div>

          <Field label="Role" value={user.access_role} testId="profile-role" />
          <Field label="Assigned Home(s)" value={homes.length ? homes.join(" + ") : "—"} testId="profile-home" />
          <Field label="Job Title" value={user.job_title || user.role_title || "—"} testId="profile-title" />
          <Field label="Classification" value={user.staff_classification || "—"} testId="profile-classification" />
          <Field label="Status"
                 value={user.active_status ? <span className="inline-flex items-center gap-1.5 text-emerald-700 font-semibold"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Active</span>
                                          : <span className="inline-flex items-center gap-1.5 text-rose-700 font-semibold"><span className="h-1.5 w-1.5 rounded-full bg-rose-500" /> Inactive</span>}
                 testId="profile-status" />
        </div>
      </section>

      <section className="nv-card p-6">
        <div className="flex items-center gap-2 text-slate-700 font-semibold text-sm mb-4">
          <Lock size={16} className="text-sky-700" /> Change password
        </div>
        <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl" data-testid="change-password-form">
          <div className="md:col-span-2">
            <label className="text-small-caps">Current password</label>
            <input type="password" required value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password"
              className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-slate-200 bg-white text-sm"
              data-testid="cp-current" />
          </div>
          <div>
            <label className="text-small-caps">New password</label>
            <input type="password" required minLength={6} value={pw1} onChange={(e) => setPw1(e.target.value)} autoComplete="new-password"
              className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-slate-200 bg-white text-sm"
              data-testid="cp-pw1" />
          </div>
          <div>
            <label className="text-small-caps">Confirm</label>
            <input type="password" required minLength={6} value={pw2} onChange={(e) => setPw2(e.target.value)} autoComplete="new-password"
              className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-slate-200 bg-white text-sm"
              data-testid="cp-pw2" />
          </div>
          <div className="md:col-span-2">
            <button type="submit" disabled={submitting}
              className="px-5 py-2.5 rounded-md bg-sky-800 hover:bg-sky-900 text-white text-sm font-semibold disabled:opacity-60"
              data-testid="cp-submit-btn">
              {submitting ? "Updating…" : "Update password"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

function Field({ label, value, testId }) {
  return (
    <div>
      <p className="text-small-caps">{label}</p>
      <p className="mt-1 text-slate-800 font-medium" data-testid={testId}>{value}</p>
    </div>
  );
}
